import { StudentNav } from '../../../common/student-nav/student-nav';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';

interface SearchCategory {
  label: string;
  icon: string;
  /** Route to navigate to with the current query, or null if not yet searchable. */
  route: string | null;
}

/**
 * Unified search results page. Reached from the nav bar's search (Enter, or
 * "See all results" in the autocomplete dropdown) regardless of which page
 * the search started from.
 *
 * There's no cross-entity search endpoint yet, so this doesn't show live
 * matches — it hands off to each category's own existing local search, all
 * of which now filter on `?q=`.
 */
@Component({
  selector: 'app-search-results',
  standalone: true,
  imports: [CommonModule, StudentNav, MatIconModule, MatCardModule],
  templateUrl: './search-results.html',
  styleUrl: './search-results.css',
})
export class SearchResults implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  query = '';

  private querySub!: Subscription;

  readonly categories: SearchCategory[] = [
    { label: 'Posts', icon: 'article', route: '/students' },
    { label: 'Network', icon: 'people', route: '/network' },
    { label: 'Messages', icon: 'chat_bubble_outline', route: '/messages' },
    { label: 'Careers', icon: 'work_outline', route: '/careers' },
    { label: 'Meetings', icon: 'perm_contact_calendar', route: '/meetings' },
  ];

  ngOnInit(): void {
    this.querySub = this.route.queryParamMap.subscribe((params) => {
      this.query = params.get('q') ?? '';
    });
  }

  ngOnDestroy(): void {
    this.querySub?.unsubscribe();
  }

  /**
   * Navigates to a category's own page with the current query, if that
   * category supports search yet.
   * @param {SearchCategory} category - The category card that was activated.
   * @returns {void}
   */
  goToCategory(category: SearchCategory): void {
    if (!category.route) return;

    this.router.navigate([category.route], { queryParams: { q: this.query || null } });
  }
}
